import type { ReactNode, ReactPortal } from 'react';
import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import FloatingBoxContext from './FloatingBoxContext';
import type { Box } from './FloatingBoxContext';
import type { FloatingBoxLocation } from './FloatingBoxContextProvider';

type FloatingBoxHandler = {
  addBox: () => void;
  removeBox: () => void;
  floatingBox: (children: ReactNode) => ReactPortal | null;
  showBox: boolean;
};

type FloatingBoxOptions = {
  /**
   * The location of the floating box.
   */
  location?: FloatingBoxLocation;
  /**
   * Whether to show the box initially, should only be set if the box is triggered by a non UI interaction,
   * such as the presence of a cookie.
   */
  initiallyShowBox?: boolean;
};

/**
 * Floating box context provider. This provider is used to add floating boxes to the
 * bottom left and bottom right of the screen.
 *
 * @param boxId The id of the floating box.
 * @param options The options for the floating box.
 * such as the presence of a cookie.
 *
 * @author Adam Ayres
 */
export default function useFloatingBox(
  boxId: string,
  options: FloatingBoxOptions = {}
): FloatingBoxHandler {
  const { addBox, removeBox, hasBox, getBoxReference } = useContext(FloatingBoxContext);
  const [box, setBox] = useState<Box>();
  const { location, initiallyShowBox } = options;

  useEffect(() => {
    // We need to use a useEffect here since the ref in the FloatingBoxContext
    // does not get set until after the floating box, in the children, are rendered
    // and thus no good way to get access to the ref for the portal.
    const currentBox = getBoxReference(boxId);
    if (currentBox) {
      setBox(currentBox);
    }
  }, [getBoxReference, boxId]);

  useEffect(() => {
    if (initiallyShowBox != null) {
      if (initiallyShowBox) {
        addBox(boxId, location);
      } else {
        removeBox(boxId);
      }
    }
  }, [addBox, boxId, initiallyShowBox, location, removeBox]);

  return {
    addBox: () => addBox(boxId, location),
    removeBox: () => removeBox(boxId),
    floatingBox: (children: ReactNode) => {
      if (box?.ref?.current) {
        return createPortal(children, box.ref.current);
      }
    },
    showBox: hasBox(boxId)
  };
}
